/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useGetEmailGroupsQuery } from "../../../../store/api/admin/adminApiSlice";
import { IEmailGroup } from "../../../../store/api/admin/types";
import { IOption } from "../../../Shared/Filter/types";
import { useSelector } from "react-redux";
import { tasksSelector } from "../../../../store/api/tasks/selector";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { useGetCustomAuditTemplatesQuery, useGetReportListQuery } from "../../../../store/api/tasks/taskApiSlice";
import { API } from "../../../../globals/globals";
import { ICustomAuditTemplate } from "../../../../store/api/tasks/types";

/** Shared helper: initializes email group state from Redux on mount (edit flow). */
const useEmailGroups = () => {
  const { data, refetch } = useGetEmailGroupsQuery();
  const [emailGroupName, setEmailGroupName] = useState<IOption[]>([]);
  const scheduledtasks = useSelector(tasksSelector);

  const emailGroupNameOptions: IOption[] =
    (data?.length > 0 && data?.map((item: IEmailGroup) => ({ title: item.id?.objectKey, value: item.id?.objectKey }))) || [];

  useEffect(() => {
    if (!!scheduledtasks?.emailGroupName) {
      let emailGroupOption: IOption[] = [];
      if (scheduledtasks?.emailGroupName.includes(",")) {
        const splitinArr = scheduledtasks.emailGroupName.split(",");
        emailGroupOption = splitinArr?.length > 0 ? splitinArr.map((item: string) => ({ title: item, value: item })) : [];
      } else {
        emailGroupOption = [{ title: scheduledtasks?.emailGroupName, value: scheduledtasks?.emailGroupName }];
      }
      setEmailGroupName(emailGroupOption);
    }
    // execute only once on mount (edit flow restore)
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { emailGroupNameOptions, emailGroupName, setEmailGroupName };
};

/** Hook for the standard Audit Reports form (report type dropdown + email group). */
export const useAuditReport = () => {
  const reportlistResponse = useGetReportListQuery();
  const auditReportList = reportlistResponse?.data || [];

  const errorMsg = useSelector(errorMsgSelector);
  const scheduledtasks = useSelector(tasksSelector);
  const emailGroups = useEmailGroups();

  return {
    errorMsg,
    scheduledtasks,
    auditReportList,
    ...emailGroups,
  };
};

/** Hook for the Custom Audit Report form (template/manual input + email group). */
export const useCustomAuditReport = () => {
  const templatesResponse = useGetCustomAuditTemplatesQuery(
    undefined,
    { pollingInterval: API.ENDPOINTS.CUSTOM_AUDIT_TEMPLATES.pollingInterval || API.POLLING_INTERVAL }
  );
  const templateOptions: IOption[] =
    (templatesResponse?.data || []).map((item: ICustomAuditTemplate) => ({
      title: item.templateName || "",
      value: item.templateName || "",
    })) || [];

  const errorMsg = useSelector(errorMsgSelector);
  const scheduledtasks = useSelector(tasksSelector);
  const emailGroups = useEmailGroups();

  return {
    errorMsg,
    scheduledtasks,
    templateOptions,
    refetchTemplates: templatesResponse.refetch,
    ...emailGroups,
  };
};
